import { getErrorString, Logger } from 'bernie-logger';
import { ILocalization } from 'bernie-l10n';

import { getMultiItemMutationImpactingKeys, SearchCriteriaModel, SearchCriteriaUtils } from 'stores/models';
import { STORE_EVENTS } from './events/store-events';

import { BasicError, MultiItemContext, PackageType } from 'models/multi-item/multi-item-types';

const TIMEOUT_MULTI_ITEM_SEARCH = 60000;
const TIMEOUT_DEFAULT = 35000;

export function getSearchTimeout(packageType?: PackageType | null) {
  return packageType ? TIMEOUT_MULTI_ITEM_SEARCH : TIMEOUT_DEFAULT;
}

export function getDefaultTimeout() {
  return TIMEOUT_DEFAULT;
}

export enum CollectionBusinessModel {
  EC = 'EC', // Expedia Collect
  HC = 'HC', // Hotel Collect
  NA = 'NA',
}

export enum MultiItemStoreStatus {
  IDLE,
  ADDING_PROPERTY_SUCCESS,
  ADDING_PROPERTY_FAILURE,
  PREPARE_CHECKOUT_PROCESSING,
  PREPARE_CHECKOUT_SUCCESS,
  PREPARE_CHECKOUT_FAILURE,
  ADDING_PROPERTY,
  FETCHING,
  FETCH_FAILURE,
  FETCH_SUCCESS,
  REMOVE_PROPERTY_SUCCESS,
  REMOVE_PROPERTY_FAILURE,
  EMPTY_CART_SUCCESS,
  EMPTY_CART_FAILURE,
  ADDING_PROPERTY_SUCCESS_CHECKOUT_NOW,
  CREATE_SESSION_SUCCESS,
  CREATE_SESSION_FAILURE,
  INITIATE_SESSION_SUCCESS,
  SELECT_PRODUCTS_SUCCESS,
  INITIATE_SESSION_FAILURE,
  SELECT_PRODUCTS_FAILURE,
}

export interface CartInfo {
  id: string;
  timestamp: number;
  type: CollectionBusinessModel;
  identifier?: string;
}

export const SHOPPING_CART_KEY = 'curr_cart';
export const MIX_MATCH_CART_KEY = 'mix_match_cart';
export const CART_EXPIRATION_IN_HOURS = 168;
export const INCOMPATIBLE_RATEPLAN = 'trying to add a rateplan with incompatible business model';

export function updateStatus(
  logger: Logger,
  setError: (error) => void,
  status: MultiItemStoreStatus,
  l10n: ILocalization,
  error?: string,
  userError?: BasicError
): MultiItemStoreStatus {
  if (logger && typeof logger.logEvent === 'function') {
    switch (status) {
      case MultiItemStoreStatus.ADDING_PROPERTY_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_ADD_PROPERTY_ERROR,
          getErrorString(new Error(`Multi Item - Adding property failure, ${error}`))
        );
        setError({ message: l10n.formatText('user.errorSummary.default.heading') });
        break;
      case MultiItemStoreStatus.FETCH_FAILURE:
        logger.logEvent(STORE_EVENTS.BEX_API_MULTI_ITEM_FETCH_ERROR, getErrorString(new Error(`Multi Item - Fetch failure, ${error}`)));
        setError({ message: l10n.formatText('user.errorSummary.default.heading') });
        break;
      case MultiItemStoreStatus.PREPARE_CHECKOUT_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_CHECKOUT_PROPERTY_ERROR,
          getErrorString(new Error(`Multi Item - Prepare checkout failure, ${error}`))
        );
        setError(userError || { message: l10n.formatText('user.errorSummary.default.heading') });
        break;
      case MultiItemStoreStatus.REMOVE_PROPERTY_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_REMOVE_PROPERTY_ERROR,
          getErrorString(new Error(`Multi Item - Remove property failure, ${error}`))
        );
        setError({ message: l10n.formatText('user.errorSummary.default.heading') });
        break;
      case MultiItemStoreStatus.CREATE_SESSION_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_CREATE_SESSION_ERROR,
          getErrorString(new Error(`Multi Item - Create session failure, ${error}`))
        );
        break;
      case MultiItemStoreStatus.INITIATE_SESSION_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_INITIATE_SESSION_ERROR,
          getErrorString(new Error(`Multi Item - Initiate session failure, ${error}`))
        );
        break;
      case MultiItemStoreStatus.SELECT_PRODUCTS_FAILURE:
        logger.logEvent(
          STORE_EVENTS.BEX_API_MULTI_ITEM_SELECT_PRODUCTS_ERROR,
          getErrorString(new Error(`Multi Item - Select products failure, ${error}`))
        );
        break;
      default:
        setError(null);
        break;
    }
  }

  return status;
}

export function getPackageType(query: any = {}): PackageType {
  return query.misId && query.packageType && (PackageType[query.packageType.toString().toUpperCase()] as PackageType);
}

export function buildMultiItemContext(query: any = {}): MultiItemContext {
  const packageType = getPackageType(query);

  return packageType
    ? {
        id: query.misId,
        packageType,
      }
    : undefined;
}

export function isStoredUserCartExpired(cartInfo: CartInfo): boolean {
  if (!cartInfo) {
    return true;
  }

  const currentTimestamp = new Date().valueOf();
  const cartTimestamp = cartInfo.timestamp;
  const cartDate = new Date(cartTimestamp);
  const cartExpiryTimestamp = cartDate.setHours(cartDate.getHours() + CART_EXPIRATION_IN_HOURS);

  return currentTimestamp > cartExpiryTimestamp;
}

export function loadCartFromStorage(): CartInfo {
  let cartInfo = JSON.parse(localStorage.getItem(SHOPPING_CART_KEY));
  if (!cartInfo) {
    cartInfo = JSON.parse(localStorage.getItem(MIX_MATCH_CART_KEY));
  }
  if (isStoredUserCartExpired(cartInfo)) {
    cartInfo = clearCartFromStorage();
  }

  return cartInfo;
}

export function clearCartFromStorage() {
  localStorage.removeItem(SHOPPING_CART_KEY);
  localStorage.removeItem(MIX_MATCH_CART_KEY);

  return null;
}

export function clearMixAndMatchCartFromStorage() {
  localStorage.removeItem(MIX_MATCH_CART_KEY);
}

export function saveCartInStorage(cartInfo: CartInfo) {
  if (cartInfo.identifier === 'mixAndMatch') {
    localStorage.setItem(MIX_MATCH_CART_KEY, JSON.stringify(cartInfo));
  } else {
    localStorage.setItem(SHOPPING_CART_KEY, JSON.stringify(cartInfo));
  }
}

export function shouldCallMultiItemSessionMutation(
  prevSearchCriteria: SearchCriteriaModel,
  newSearchCriteria: SearchCriteriaModel,
  packageType: PackageType
) {
  if (!prevSearchCriteria || !newSearchCriteria) {
    return false;
  }

  return SearchCriteriaUtils.hasSearchCriteriaChanged(
    prevSearchCriteria,
    newSearchCriteria,
    getMultiItemMutationImpactingKeys(packageType)
  );
}
